import { create } from "zustand";

/**
 * User-selectable theme preference. "system" follows the operating system's
 * `prefers-color-scheme` setting and updates live when it changes.
 */
export type Theme = "light" | "dark" | "system";

/** Concrete theme actually applied to the document. */
export type ResolvedTheme = "light" | "dark";

/** localStorage key holding the persisted theme preference. */
export const THEME_STORAGE_KEY = "forgepoint-theme";

/** Media query used to detect the OS-level dark mode preference. */
const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

interface ThemeState {
  /** The user's selected preference; may be "system". */
  theme: Theme;
  /** The concrete theme applied to the document ("light" or "dark"). */
  resolvedTheme: ResolvedTheme;
}

interface ThemeActions {
  /** Sets the preference, resolves it, applies it to the document, and persists it. */
  setTheme: (theme: Theme) => void;
}

/**
 * Reads the persisted theme preference from localStorage, defaulting to
 * "system" so new users follow their operating system setting.
 * @returns The stored preference, or "system" when absent or invalid.
 */
function readStoredTheme(): Theme {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

/**
 * Resolves a preference to the concrete theme to render. "system" is resolved
 * against the current OS `prefers-color-scheme` value.
 * @param theme - The preference to resolve.
 * @returns The concrete theme to apply.
 */
function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === "system") {
    return window.matchMedia(DARK_MEDIA_QUERY).matches ? "dark" : "light";
  }
  return theme;
}

/**
 * Applies a preference to the document root by toggling the `dark` class and
 * setting `color-scheme` to the resolved theme, then persists the preference.
 * @param theme - The preference to apply.
 * @returns The concrete theme that was applied.
 */
function applyTheme(theme: Theme): ResolvedTheme {
  const resolved = resolveTheme(theme);
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
  localStorage.setItem(THEME_STORAGE_KEY, theme);
  return resolved;
}

/**
 * Global theme store persisting the user's preference to localStorage and
 * syncing the resolved theme to the document root.
 */
export const useThemeStore = create<ThemeState & ThemeActions>((set) => {
  const initial = readStoredTheme();
  return {
    theme: initial,
    resolvedTheme: resolveTheme(initial),
    setTheme: (theme) => {
      const resolvedTheme = applyTheme(theme);
      set({ theme, resolvedTheme });
    },
  };
});

/**
 * Re-applies the theme when the OS preference changes, but only while the
 * user's preference is "system".
 */
function handleSystemPreferenceChange(): void {
  const { theme } = useThemeStore.getState();
  if (theme !== "system") {
    return;
  }
  const resolvedTheme = applyTheme("system");
  useThemeStore.setState({ resolvedTheme });
}

/**
 * Applies the persisted theme to the document and subscribes to OS
 * `prefers-color-scheme` changes. Call once on app startup so the store state
 * and the DOM class stay in sync before the first render.
 */
export function initTheme(): void {
  const theme = readStoredTheme();
  const resolvedTheme = applyTheme(theme);
  useThemeStore.setState({ theme, resolvedTheme });
  window
    .matchMedia(DARK_MEDIA_QUERY)
    .addEventListener("change", handleSystemPreferenceChange);
}
